/**
 * Admin dashboard page.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import AdminIdPManager from '../components/AdminIdPManager';
import { config } from '../config';

interface Stats {
  users: number;
  organizations: number;
  apiKeys: number;
  services: number;
}

interface User {
  id: string;
  email: string;
  name: { first: string; last: string } | null;
  created_at: string;
  is_admin: boolean;
}

interface Organization {
  id: string;
  name: string;
  member_count: number;
  created_at: string;
  code: string | null;
}

interface OrgMember {
  id: string;
  email: string;
  name: { first: string; last: string } | null;
  role: string;
  default_organization: string | null;
}

interface ApiKey {
  id: string;
  name: string;
  service: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
}

interface Service {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  org_count: number;
}

interface ServiceOrg {
  id: string;
  name: string;
}

interface ServiceApiKey {
  id: string;
  name: string;
  description: string | null;
  service_id: string;
  key_prefix: string;
  status: string;
  permissions: string[];
  created_at: string;
  created_by: string;
  last_used_at: string | null;
}

interface Device {
  id: string;
  name: string;
  description: string | null;
  organization_id: string;
  organization_name: string | null;
  service_id: string;
  service_name: string | null;
  device_type: string | null;
  hardware_id: string | null;
  firmware_version: string | null;
  metadata: Record<string, unknown>;
  token_prefix: string | null;
  status: 'active' | 'suspended' | 'revoked';
  created_at: string;
  created_by: string | null;
  last_seen_at: string | null;
}

interface DeviceType {
  type: string;
  category: string | null;
  label: string;
  description: string;
}

interface Application {
  id: string;
  name: string;
  description: string | null;
  organization_id: string;
  organization_name: string | null;
  client_id: string;
  client_secret_prefix: string;
  owner_user_id: string;
  allowed_services: string[];
  rate_limit_tier: string;
  status: 'active' | 'suspended' | 'revoked';
  created_at: string;
  last_used_at: string | null;
}

export default function Admin() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, identity } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'organizations' | 'apikeys' | 'services' | 'devices' | 'applications' | 'sessions' | 'kyc' | 'idp'>('overview');
  const [stats, setStats] = useState<Stats>({ users: 0, organizations: 0, apiKeys: 0, services: 0 });
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Modal state for creating API key
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyService, setNewKeyService] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  // Modal state for creating service
  const [showCreateServiceModal, setShowCreateServiceModal] = useState(false);
  const [newServiceId, setNewServiceId] = useState('');
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceDescription, setNewServiceDescription] = useState('');

  // Modal state for managing service organization access
  const [showServiceOrgsModal, setShowServiceOrgsModal] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [serviceOrgs, setServiceOrgs] = useState<ServiceOrg[]>([]);
  const [loadingServiceOrgs, setLoadingServiceOrgs] = useState(false);

  // Modal state for granting org access
  const [showGrantOrgAccessModal, setShowGrantOrgAccessModal] = useState(false);
  const [selectedOrgForAccess, setSelectedOrgForAccess] = useState('');

  // Modal state for creating organization
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgOwner, setNewOrgOwner] = useState('');

  // Modal state for managing org members
  const [showOrgMembersModal, setShowOrgMembersModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [loadingOrgMembers, setLoadingOrgMembers] = useState(false);

  // Modal state for adding org member
  const [showAddOrgMemberModal, setShowAddOrgMemberModal] = useState(false);
  const [selectedUserForOrg, setSelectedUserForOrg] = useState('');
  const [selectedRoleForOrg, setSelectedRoleForOrg] = useState('member');

  // Modal state for creating device
  const [showCreateDeviceModal, setShowCreateDeviceModal] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceDescription, setNewDeviceDescription] = useState('');
  const [newDeviceOrgId, setNewDeviceOrgId] = useState('');
  const [newDeviceServiceId, setNewDeviceServiceId] = useState('');
  const [newDeviceType, setNewDeviceType] = useState('');
  const [newDeviceHardwareId, setNewDeviceHardwareId] = useState('');
  const [createdDeviceToken, setCreatedDeviceToken] = useState<string | null>(null);

  // Modal state for viewing device details
  const [showDeviceDetailsModal, setShowDeviceDetailsModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  // Modal state for creating application
  const [showCreateAppModal, setShowCreateAppModal] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [newAppDescription, setNewAppDescription] = useState('');
  const [newAppOrgId, setNewAppOrgId] = useState('');
  const [newAppServices, setNewAppServices] = useState<string[]>([]);
  const [newAppTier, setNewAppTier] = useState('starter');
  const [createdAppCredentials, setCreatedAppCredentials] = useState<{ client_id: string; client_secret: string } | null>(null);

  // Modal state for service API keys
  const [showServiceApiKeysModal, setShowServiceApiKeysModal] = useState(false);
  const [serviceApiKeys, setServiceApiKeys] = useState<ServiceApiKey[]>([]);
  const [loadingServiceApiKeys, setLoadingServiceApiKeys] = useState(false);
  const [showCreateServiceApiKeyModal, setShowCreateServiceApiKeyModal] = useState(false);
  const [newServiceApiKeyName, setNewServiceApiKeyName] = useState('');
  const [newServiceApiKeyDescription, setNewServiceApiKeyDescription] = useState('');
  const [newServiceApiKeyPermissions, setNewServiceApiKeyPermissions] = useState<string[]>([]);
  const [createdServiceApiKey, setCreatedServiceApiKey] = useState<string | null>(null);

  // Check if user is admin
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login?return_to=/admin', { replace: true });
      return;
    }

    checkAdminStatus();
  }, [isAuthenticated, authLoading, navigate]);

  const checkAdminStatus = async () => {
    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/check`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          loadDashboardData();
        }
      } else {
        setIsAdmin(false);
      }
    } catch (err) {
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [statsRes, usersRes, orgsRes, keysRes, servicesRes, devicesRes, appsRes, deviceTypesRes] = await Promise.all([
        fetch(`${config.gatewayUrl}/v1/admin/stats`, { credentials: 'include' }),
        fetch(`${config.gatewayUrl}/v1/admin/users`, { credentials: 'include' }),
        fetch(`${config.gatewayUrl}/v1/admin/organizations`, { credentials: 'include' }),
        fetch(`${config.gatewayUrl}/v1/admin/api-keys`, { credentials: 'include' }),
        fetch(`${config.gatewayUrl}/v1/admin/services`, { credentials: 'include' }),
        fetch(`${config.gatewayUrl}/v1/admin/devices`, { credentials: 'include' }),
        fetch(`${config.gatewayUrl}/v1/admin/applications`, { credentials: 'include' }),
        fetch(`${config.gatewayUrl}/v1/device-types`),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers((await usersRes.json()).users || []);
      if (orgsRes.ok) setOrganizations((await orgsRes.json()).organizations || []);
      if (keysRes.ok) setApiKeys((await keysRes.json()).api_keys || []);
      if (servicesRes.ok) setServices((await servicesRes.json()).services || []);
      if (devicesRes.ok) setDevices((await devicesRes.json()).devices || []);
      if (appsRes.ok) setApplications((await appsRes.json()).applications || []);
      if (deviceTypesRes.ok) setDeviceTypes((await deviceTypesRes.json()).device_types || []);
    } catch (err) {
      setError('Failed to load dashboard data');
    }
  };

  const toggleUserAdmin = async (userId: string, makeAdmin: boolean) => {
    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/users/${userId}/admin`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_admin: makeAdmin }),
      });

      if (response.ok) {
        setUsers(users.map(u => u.id === userId ? { ...u, is_admin: makeAdmin } : u));
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update user');
      }
    } catch (err) {
      setError('Failed to update user');
    }
  };

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim() || !newKeyService.trim()) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/api-keys`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName.trim(), service: newKeyService.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedKey(data.key);
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create API key');
      }
    } catch (err) {
      setError('Failed to create API key');
    }
  };

  const closeKeyModal = () => {
    setShowCreateKeyModal(false);
    setNewKeyName('');
    setNewKeyService('');
    setCreatedKey(null);
  };

  const revokeApiKey = async (keyId: string, keyName: string) => {
    if (!confirm(`Revoke API key "${keyName}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/api-keys/${keyId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setApiKeys(apiKeys.filter(k => k.id !== keyId));
        loadDashboardData();
      } else {
        setError('Failed to revoke API key');
      }
    } catch (err) {
      setError('Failed to revoke API key');
    }
  };

  // Service management functions
  const handleCreateService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newServiceId.trim() || !newServiceName.trim()) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/services`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newServiceId.trim().toLowerCase().replace(/\s+/g, '-'),
          name: newServiceName.trim(),
          description: newServiceDescription.trim() || null,
        }),
      });

      if (response.ok) {
        setShowCreateServiceModal(false);
        setNewServiceId('');
        setNewServiceName('');
        setNewServiceDescription('');
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create service');
      }
    } catch (err) {
      setError('Failed to create service');
    }
  };

  const deleteService = async (serviceId: string, serviceName: string) => {
    if (!confirm(`Delete service "${serviceName}"? This will revoke all user access.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/services/${serviceId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        loadDashboardData();
      } else {
        setError('Failed to delete service');
      }
    } catch (err) {
      setError('Failed to delete service');
    }
  };

  const openServiceOrgs = async (service: Service) => {
    setSelectedService(service);
    setShowServiceOrgsModal(true);
    setLoadingServiceOrgs(true);

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/services/${service.id}/organizations`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setServiceOrgs(data.organizations || []);
      } else {
        setError('Failed to load service organizations');
      }
    } catch (err) {
      setError('Failed to load service organizations');
    } finally {
      setLoadingServiceOrgs(false);
    }
  };

  const grantOrgAccess = async () => {
    if (!selectedService || !selectedOrgForAccess) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/services/${selectedService.id}/organizations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: selectedOrgForAccess }),
      });

      if (response.ok) {
        setShowGrantOrgAccessModal(false);
        setSelectedOrgForAccess('');
        openServiceOrgs(selectedService);
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to grant access');
      }
    } catch (err) {
      setError('Failed to grant access');
    }
  };

  const revokeOrgAccess = async (orgId: string, orgName: string) => {
    if (!selectedService) return;
    if (!confirm(`Revoke ${orgName}'s access to ${selectedService.name}?`)) return;

    try {
      const response = await fetch(
        `${config.gatewayUrl}/v1/admin/services/${selectedService.id}/organizations/${orgId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (response.ok) {
        openServiceOrgs(selectedService);
        loadDashboardData();
      } else {
        setError('Failed to revoke access');
      }
    } catch (err) {
      setError('Failed to revoke access');
    }
  };

  // Device management functions
  const handleCreateDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeviceName.trim() || !newDeviceOrgId || !newDeviceServiceId) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/devices`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newDeviceName.trim(),
          description: newDeviceDescription.trim() || null,
          organization_id: newDeviceOrgId,
          service_id: newDeviceServiceId,
          device_type: newDeviceType.trim() || null,
          hardware_id: newDeviceHardwareId.trim() || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedDeviceToken(data.token);
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create device');
      }
    } catch (err) {
      setError('Failed to create device');
    }
  };

  const closeDeviceModal = () => {
    setShowCreateDeviceModal(false);
    setNewDeviceName('');
    setNewDeviceDescription('');
    setNewDeviceOrgId('');
    setNewDeviceServiceId('');
    setNewDeviceType('');
    setNewDeviceHardwareId('');
    setCreatedDeviceToken(null);
  };

  const suspendDevice = async (deviceId: string, deviceName: string) => {
    if (!confirm(`Suspend device "${deviceName}"? It will no longer be able to authenticate.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/devices/${deviceId}/suspend`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        loadDashboardData();
      } else {
        setError('Failed to suspend device');
      }
    } catch (err) {
      setError('Failed to suspend device');
    }
  };

  const reactivateDevice = async (deviceId: string) => {
    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/devices/${deviceId}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        loadDashboardData();
      } else {
        setError('Failed to reactivate device');
      }
    } catch (err) {
      setError('Failed to reactivate device');
    }
  };

  const revokeDevice = async (deviceId: string, deviceName: string) => {
    if (!confirm(`Revoke device "${deviceName}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/devices/${deviceId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        loadDashboardData();
        if (selectedDevice?.id === deviceId) {
          setShowDeviceDetailsModal(false);
          setSelectedDevice(null);
        }
      } else {
        setError('Failed to revoke device');
      }
    } catch (err) {
      setError('Failed to revoke device');
    }
  };

  const regenerateDeviceToken = async (deviceId: string, deviceName: string) => {
    if (!confirm(`Regenerate token for "${deviceName}"? The old token will stop working immediately.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/devices/${deviceId}/regenerate-token`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedDeviceToken(data.token);
        setShowCreateDeviceModal(true);
        loadDashboardData();
      } else {
        setError('Failed to regenerate token');
      }
    } catch (err) {
      setError('Failed to regenerate token');
    }
  };

  const openDeviceDetails = (device: Device) => {
    setSelectedDevice(device);
    setShowDeviceDetailsModal(true);
  };

  // Service API Key management functions
  const openServiceApiKeys = async (service: Service) => {
    setSelectedService(service);
    setShowServiceApiKeysModal(true);
    setLoadingServiceApiKeys(true);

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/services/${service.id}/api-keys`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setServiceApiKeys(data.api_keys || []);
      } else {
        setError('Failed to load service API keys');
      }
    } catch (err) {
      setError('Failed to load service API keys');
    } finally {
      setLoadingServiceApiKeys(false);
    }
  };

  const handleCreateServiceApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || !newServiceApiKeyName.trim() || newServiceApiKeyPermissions.length === 0) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/services/${selectedService.id}/api-keys`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newServiceApiKeyName.trim(),
          description: newServiceApiKeyDescription.trim() || null,
          permissions: newServiceApiKeyPermissions,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedServiceApiKey(data.key);
        openServiceApiKeys(selectedService);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create service API key');
      }
    } catch (err) {
      setError('Failed to create service API key');
    }
  };

  const closeServiceApiKeyModal = () => {
    setShowCreateServiceApiKeyModal(false);
    setNewServiceApiKeyName('');
    setNewServiceApiKeyDescription('');
    setNewServiceApiKeyPermissions([]);
    setCreatedServiceApiKey(null);
  };

  const revokeServiceApiKey = async (keyId: string, keyName: string) => {
    if (!selectedService) return;
    if (!confirm(`Revoke service API key "${keyName}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(
        `${config.gatewayUrl}/v1/admin/services/${selectedService.id}/api-keys/${keyId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (response.ok) {
        openServiceApiKeys(selectedService);
      } else {
        setError('Failed to revoke service API key');
      }
    } catch (err) {
      setError('Failed to revoke service API key');
    }
  };

  const togglePermission = (permission: string) => {
    setNewServiceApiKeyPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  // Application management functions
  const handleCreateApp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAppName.trim() || !newAppOrgId) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/applications`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newAppName.trim(),
          description: newAppDescription.trim() || null,
          organization_id: newAppOrgId,
          allowed_services: newAppServices,
          rate_limit_tier: newAppTier,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedAppCredentials({ client_id: data.client_id, client_secret: data.client_secret });
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create application');
      }
    } catch (err) {
      setError('Failed to create application');
    }
  };

  const closeAppModal = () => {
    setShowCreateAppModal(false);
    setNewAppName('');
    setNewAppDescription('');
    setNewAppOrgId('');
    setNewAppServices([]);
    setNewAppTier('starter');
    setCreatedAppCredentials(null);
  };

  const toggleAppService = (serviceId: string) => {
    setNewAppServices(prev =>
      prev.includes(serviceId)
        ? prev.filter(s => s !== serviceId)
        : [...prev, serviceId]
    );
  };

  const suspendApp = async (appId: string, appName: string) => {
    if (!confirm(`Suspend application "${appName}"? It can be reactivated later.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/applications/${appId}/suspend`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to suspend application');
      }
    } catch (err) {
      setError('Failed to suspend application');
    }
  };

  const reactivateApp = async (appId: string) => {
    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/applications/${appId}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to reactivate application');
      }
    } catch (err) {
      setError('Failed to reactivate application');
    }
  };

  const revokeApp = async (appId: string, appName: string) => {
    if (!confirm(`Revoke application "${appName}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/applications/${appId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to revoke application');
      }
    } catch (err) {
      setError('Failed to revoke application');
    }
  };

  const regenerateAppSecret = async (appId: string, appName: string) => {
    if (!confirm(`Regenerate secret for "${appName}"? The old secret will stop working immediately.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/applications/${appId}/regenerate-secret`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedAppCredentials({ client_id: data.client_id, client_secret: data.client_secret });
        setShowCreateAppModal(true);
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to regenerate secret');
      }
    } catch (err) {
      setError('Failed to regenerate secret');
    }
  };

  // Organization management functions
  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/organizations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newOrgName.trim(),
          owner_user_id: newOrgOwner || null,
        }),
      });

      if (response.ok) {
        setShowCreateOrgModal(false);
        setNewOrgName('');
        setNewOrgOwner('');
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create organization');
      }
    } catch (err) {
      setError('Failed to create organization');
    }
  };

  const deleteOrg = async (orgId: string, orgName: string) => {
    if (!confirm(`Delete organization "${orgName}"? This will remove all members.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/organizations/${orgId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        loadDashboardData();
      } else {
        setError('Failed to delete organization');
      }
    } catch (err) {
      setError('Failed to delete organization');
    }
  };

  const openOrgMembers = async (org: Organization) => {
    setSelectedOrg(org);
    setShowOrgMembersModal(true);
    setLoadingOrgMembers(true);

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/organizations/${org.id}/members`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setOrgMembers(data.members || []);
      } else {
        setError('Failed to load organization members');
      }
    } catch (err) {
      setError('Failed to load organization members');
    } finally {
      setLoadingOrgMembers(false);
    }
  };

  const addOrgMember = async () => {
    if (!selectedOrg || !selectedUserForOrg) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/organizations/${selectedOrg.id}/members`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedUserForOrg,
          role: selectedRoleForOrg,
        }),
      });

      if (response.ok) {
        setShowAddOrgMemberModal(false);
        setSelectedUserForOrg('');
        setSelectedRoleForOrg('member');
        openOrgMembers(selectedOrg);
        loadDashboardData();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to add member');
      }
    } catch (err) {
      setError('Failed to add member');
    }
  };

  const setMemberDefaultOrg = async (userId: string, orgId: string) => {
    try {
      const response = await fetch(
        `${config.gatewayUrl}/v1/admin/users/${userId}/default-organization`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ organization_id: orgId }),
        }
      );

      if (response.ok && selectedOrg) {
        openOrgMembers(selectedOrg); // Refresh the members list
      } else {
        setError('Failed to set default organization');
      }
    } catch (err) {
      setError('Failed to set default organization');
    }
  };

  const removeOrgMember = async (userId: string, userEmail: string) => {
    if (!selectedOrg) return;
    if (!confirm(`Remove ${userEmail} from ${selectedOrg.name}?`)) return;

    try {
      const response = await fetch(
        `${config.gatewayUrl}/v1/admin/organizations/${selectedOrg.id}/members/${userId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (response.ok) {
        openOrgMembers(selectedOrg);
        loadDashboardData();
      } else {
        setError('Failed to remove member');
      }
    } catch (err) {
      setError('Failed to remove member');
    }
  };

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">Access Denied</h1>
          <p className="text-content-secondary mb-4">You don't have admin privileges.</p>
          <Link to="/settings" className="btn-primary inline-block">
            Go to Settings
          </Link>
        </div>
      </div>
    );
  }

  // Get orgs not already having access to selected service
  const orgsWithoutAccess = organizations.filter(
    o => !serviceOrgs.some(so => so.id === o.id)
  );

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Admin Dashboard</h1>
          <p className="text-content-secondary mt-1">Platform administration — users, services, keys, and organizations (system-wide view)</p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="card text-center">
            <div className="text-3xl font-bold text-orbital-600">{stats.users}</div>
            <div className="text-sm text-content-secondary">Users</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-orbital-600">{services.length}</div>
            <div className="text-sm text-content-secondary">Services</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-orbital-600">{apiKeys.length}</div>
            <div className="text-sm text-content-secondary">API Keys</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-orbital-600">{devices.length}</div>
            <div className="text-sm text-content-secondary">Devices</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-orbital-600">{stats.organizations}</div>
            <div className="text-sm text-content-secondary">Organizations</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex space-x-8 overflow-x-auto">
            {(['overview', 'users', 'services', 'apikeys', 'devices', 'applications', 'sessions', 'kyc', 'organizations', 'idp'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-orbital-600 text-orbital-600'
                    : 'border-transparent text-content-tertiary hover:text-content-primary'
                }`}
              >
                {tab === 'apikeys' ? 'Platform Keys (op_)' : tab === 'sessions' ? 'Agent Sessions' : tab === 'kyc' ? 'KYC' : tab === 'applications' ? 'OAuth2 Apps' : tab === 'idp' ? 'Identity Providers' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-medium text-content-primary mb-4">Users</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Role</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.slice(0, 5).map((user) => (
                      <tr key={user.id}>
                        <td className="px-4 py-2 text-sm text-content-primary">{user.email}</td>
                        <td className="px-4 py-2 text-sm text-content-secondary">
                          {user.name?.first} {user.name?.last}
                        </td>
                        <td className="px-4 py-2">
                          {user.is_admin ? (
                            <span className="px-2 py-1 text-xs font-medium bg-orbital-100 dark:bg-orbital-900/40 text-orbital-800 dark:text-orbital-300 rounded">Admin</span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium bg-surface-tertiary text-content-secondary rounded">User</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-medium text-content-primary mb-4">Registered Services</h3>
              {services.length === 0 ? (
                <p className="text-content-tertiary text-center py-4">No services registered yet.</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {services.map((service) => (
                    <div key={service.id} className="border rounded-lg p-4">
                      <h4 className="font-medium text-content-primary">{service.name}</h4>
                      <p className="text-sm text-content-tertiary">{service.id}</p>
                      <p className="text-sm text-orbital-600 mt-2">{service.org_count} organizations</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="card">
            <h3 className="text-lg font-medium text-content-primary mb-4">All Users</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Created</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Role</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-4 py-2 text-sm text-content-primary">{user.email}</td>
                      <td className="px-4 py-2 text-sm text-content-secondary">
                        {user.name?.first} {user.name?.last}
                      </td>
                      <td className="px-4 py-2 text-sm text-content-tertiary">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2">
                        {user.is_admin ? (
                          <span className="px-2 py-1 text-xs font-medium bg-orbital-100 dark:bg-orbital-900/40 text-orbital-800 dark:text-orbital-300 rounded">Admin</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-surface-tertiary text-content-secondary rounded">User</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {user.id !== identity?.id && (
                          <button
                            onClick={() => toggleUserAdmin(user.id, !user.is_admin)}
                            className={`text-sm ${user.is_admin ? 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300' : 'text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300'}`}
                          >
                            {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-content-primary">Registered Services</h3>
              <button onClick={() => setShowCreateServiceModal(true)} className="btn-primary text-sm">
                Register Service
              </button>
            </div>
            {services.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-content-tertiary mb-4">No services registered yet.</p>
                <p className="text-sm text-content-tertiary">Register a service to control user access to your applications.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Service ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Description</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Organizations</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {services.map((service) => (
                      <tr key={service.id}>
                        <td className="px-4 py-2 text-sm text-content-primary font-mono">{service.id}</td>
                        <td className="px-4 py-2 text-sm text-content-primary font-medium">{service.name}</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary">{service.description || '-'}</td>
                        <td className="px-4 py-2 text-sm text-content-secondary">{service.org_count}</td>
                        <td className="px-4 py-2 space-x-3">
                          <button
                            onClick={() => openServiceApiKeys(service)}
                            className="text-sm text-[var(--accent,#3D8FBF)] hover:text-[var(--accent-light,#5DA9D4)]"
                          >
                            API Keys
                          </button>
                          <button
                            onClick={() => openServiceOrgs(service)}
                            className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
                          >
                            Organizations
                          </button>
                          <button
                            onClick={() => deleteService(service.id, service.name)}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'apikeys' && (
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-medium text-content-primary">Platform Keys (op_)</h3>
                <p className="text-sm text-content-tertiary">Global keys for session/token validation. Services use these in X-API-Key header.</p>
              </div>
              <button onClick={() => setShowCreateKeyModal(true)} className="btn-primary text-sm">
                Create API Key
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Service</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Key Prefix</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Created</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Last Used</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {apiKeys.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-content-tertiary">
                        No API keys yet. Create one to authenticate your backend services.
                      </td>
                    </tr>
                  ) : (
                    apiKeys.map((key) => (
                      <tr key={key.id}>
                        <td className="px-4 py-2 text-sm text-content-primary font-medium">{key.name}</td>
                        <td className="px-4 py-2 text-sm text-content-secondary">{key.service}</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary font-mono">{key.prefix}...</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary">
                          {new Date(key.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 text-sm text-content-tertiary">
                          {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => revokeApiKey(key.id, key.name)}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-content-primary">IoT/Hardware Devices</h3>
              <button onClick={() => setShowCreateDeviceModal(true)} className="btn-primary text-sm">
                Register Device
              </button>
            </div>
            {devices.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-content-tertiary mb-4">No devices registered yet.</p>
                <p className="text-sm text-content-tertiary">Register devices to authenticate IoT/hardware systems with token-based auth.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Organization</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Service</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Token</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Last Seen</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {devices.map((device) => (
                      <tr key={device.id}>
                        <td className="px-4 py-2 text-sm text-content-primary font-medium">
                          <button
                            onClick={() => openDeviceDetails(device)}
                            className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 hover:underline"
                          >
                            {device.name}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-sm text-content-secondary">{device.device_type || '-'}</td>
                        <td className="px-4 py-2 text-sm text-content-secondary">{device.organization_name || device.organization_id}</td>
                        <td className="px-4 py-2 text-sm text-content-secondary">{device.service_name || device.service_id}</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary font-mono">
                          {device.token_prefix ? `${device.token_prefix}...` : '-'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            device.status === 'active' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' :
                            device.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                          }`}>
                            {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-content-tertiary">
                          {device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : 'Never'}
                        </td>
                        <td className="px-4 py-2 space-x-2">
                          {device.status === 'active' && (
                            <button
                              onClick={() => suspendDevice(device.id, device.name)}
                              className="text-sm text-yellow-600 hover:text-yellow-800"
                            >
                              Suspend
                            </button>
                          )}
                          {device.status === 'suspended' && (
                            <button
                              onClick={() => reactivateDevice(device.id)}
                              className="text-sm text-green-600 hover:text-green-800"
                            >
                              Reactivate
                            </button>
                          )}
                          {device.status !== 'revoked' && (
                            <button
                              onClick={() => revokeDevice(device.id, device.name)}
                              className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-content-primary">OAuth2 Applications</h3>
              <button onClick={() => setShowCreateAppModal(true)} className="btn-primary text-sm">
                Register Application
              </button>
            </div>
            {applications.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-content-tertiary mb-4">No applications registered yet.</p>
                <p className="text-sm text-content-tertiary">Register applications to enable OAuth2 client_credentials access to services.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Organization</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Client ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Services</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Tier</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Last Used</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {applications.map((app) => (
                      <tr key={app.id}>
                        <td className="px-4 py-2 text-sm text-content-primary font-medium">
                          {app.name}
                          {app.description && (
                            <p className="text-xs text-content-tertiary truncate max-w-[200px]">{app.description}</p>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-content-secondary">{app.organization_name || app.organization_id}</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary font-mono text-xs">{app.client_id}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {app.allowed_services.length > 0 ? app.allowed_services.map(s => (
                              <span key={s} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">{s}</span>
                            )) : (
                              <span className="text-xs text-content-tertiary">All</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-0.5 text-xs bg-surface-tertiary text-gray-800 rounded">{app.rate_limit_tier}</span>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            app.status === 'active' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' :
                            app.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                          }`}>
                            {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-content-tertiary">
                          {app.last_used_at ? new Date(app.last_used_at).toLocaleString() : 'Never'}
                        </td>
                        <td className="px-4 py-2 space-x-2">
                          {app.status !== 'revoked' && (
                            <button
                              onClick={() => regenerateAppSecret(app.id, app.name)}
                              className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
                            >
                              Regen Secret
                            </button>
                          )}
                          {app.status === 'active' && (
                            <button
                              onClick={() => suspendApp(app.id, app.name)}
                              className="text-sm text-yellow-600 hover:text-yellow-800"
                            >
                              Suspend
                            </button>
                          )}
                          {app.status === 'suspended' && (
                            <button
                              onClick={() => reactivateApp(app.id)}
                              className="text-sm text-green-600 hover:text-green-800"
                            >
                              Reactivate
                            </button>
                          )}
                          {app.status !== 'revoked' && (
                            <button
                              onClick={() => revokeApp(app.id, app.name)}
                              className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Organizations Tab */}
        {activeTab === 'organizations' && (
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-content-primary">All Organizations</h3>
              <button onClick={() => setShowCreateOrgModal(true)} className="btn-primary text-sm">
                Create Organization
              </button>
            </div>
            {organizations.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-content-tertiary mb-4">No organizations yet.</p>
                <p className="text-sm text-content-tertiary">Organizations are created when users register with a company name, or you can create one manually.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Invite Code</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Members</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Created</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {organizations.map((org) => (
                      <tr key={org.id}>
                        <td className="px-4 py-2 text-sm text-content-primary font-medium">{org.name}</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary font-mono">{org.id}</td>
                        <td className="px-4 py-2 text-sm text-content-secondary font-mono">{org.code || '-'}</td>
                        <td className="px-4 py-2 text-sm text-content-secondary">{org.member_count}</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary">
                          {new Date(org.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 space-x-3">
                          <button
                            onClick={() => openOrgMembers(org)}
                            className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
                          >
                            Manage Members
                          </button>
                          <button
                            onClick={() => deleteOrg(org.id, org.name)}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Create API Key Modal */}
        {showCreateKeyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              {createdKey ? (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">API Key Created</h3>
                  <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 dark:text-green-300 mb-2 font-medium">Save this key - it won't be shown again!</p>
                    <code className="block bg-surface-primary border rounded p-2 text-sm break-all select-all">
                      {createdKey}
                    </code>
                  </div>
                  <button onClick={closeKeyModal} className="btn-primary w-full">
                    Done
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">Create API Key</h3>
                  <form onSubmit={handleCreateApiKey} className="space-y-4">
                    <div>
                      <label htmlFor="keyName" className="block text-sm font-medium text-content-primary mb-1">
                        Key Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="keyName"
                        type="text"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        placeholder="e.g., Production API"
                        className="input-field"
                        required
                      />
                      <p className="text-xs text-content-tertiary mt-1">A descriptive name to identify this key</p>
                    </div>
                    <div>
                      <label htmlFor="keyService" className="block text-sm font-medium text-content-primary mb-1">
                        Service <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="keyService"
                        value={newKeyService}
                        onChange={(e) => setNewKeyService(e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Select a service...</option>
                        {services.map((svc) => (
                          <option key={svc.id} value={svc.id}>{svc.name} ({svc.id})</option>
                        ))}
                      </select>
                      <p className="text-xs text-content-tertiary mt-1">The service this key will authenticate</p>
                    </div>
                    <div className="flex space-x-3">
                      <button type="button" onClick={closeKeyModal} className="flex-1 btn-secondary">
                        Cancel
                      </button>
                      <button type="submit" className="flex-1 btn-primary">
                        Create Key
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Create Service Modal */}
        {showCreateServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-content-primary mb-4">Register Service</h3>
              <form onSubmit={handleCreateService} className="space-y-4">
                <div>
                  <label htmlFor="serviceId" className="block text-sm font-medium text-content-primary mb-1">
                    Service ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="serviceId"
                    type="text"
                    value={newServiceId}
                    onChange={(e) => setNewServiceId(e.target.value)}
                    placeholder="e.g., my-app"
                    className="input-field"
                    required
                  />
                  <p className="text-xs text-content-tertiary mt-1">Unique identifier (lowercase, hyphens allowed)</p>
                </div>
                <div>
                  <label htmlFor="serviceName" className="block text-sm font-medium text-content-primary mb-1">
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="serviceName"
                    type="text"
                    value={newServiceName}
                    onChange={(e) => setNewServiceName(e.target.value)}
                    placeholder="e.g., My Application"
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="serviceDesc" className="block text-sm font-medium text-content-primary mb-1">
                    Description
                  </label>
                  <input
                    id="serviceDesc"
                    type="text"
                    value={newServiceDescription}
                    onChange={(e) => setNewServiceDescription(e.target.value)}
                    placeholder="e.g., Main customer-facing application"
                    className="input-field"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => { setShowCreateServiceModal(false); setNewServiceId(''); setNewServiceName(''); setNewServiceDescription(''); }}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn-primary">
                    Register
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Service Organizations Modal */}
        {showServiceOrgsModal && selectedService && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-auto">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-medium text-content-primary">Manage Access: {selectedService.name}</h3>
                  <p className="text-sm text-content-tertiary">{selectedService.id}</p>
                </div>
                <button
                  onClick={() => { setShowServiceOrgsModal(false); setSelectedService(null); setServiceOrgs([]); }}
                  className="text-content-tertiary hover:text-content-secondary"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <button
                onClick={() => setShowGrantOrgAccessModal(true)}
                className="btn-primary text-sm mb-4"
              >
                Grant Access to Organization
              </button>

              {loadingServiceOrgs ? (
                <div className="text-center py-8">
                  <LoadingSpinner text="Loading organizations..." />
                </div>
              ) : serviceOrgs.length === 0 ? (
                <p className="text-content-tertiary text-center py-8">No organizations have access to this service yet.</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Organization</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {serviceOrgs.map((org) => (
                      <tr key={org.id}>
                        <td className="px-4 py-2 text-sm text-content-primary font-medium">{org.name}</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary font-mono">{org.id}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => revokeOrgAccess(org.id, org.name)}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Service API Keys Modal */}
        {showServiceApiKeysModal && selectedService && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-3xl w-full p-6 max-h-[80vh] overflow-auto">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-medium text-content-primary">Service Automation Keys: {selectedService.name}</h3>
                  <p className="text-sm text-content-tertiary">Scoped keys for backend automation (osk_ prefix) — org/device management, token issuance</p>
                </div>
                <button
                  onClick={() => { setShowServiceApiKeysModal(false); setSelectedService(null); setServiceApiKeys([]); }}
                  className="text-content-tertiary hover:text-content-secondary"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <button
                onClick={() => setShowCreateServiceApiKeyModal(true)}
                className="btn-primary text-sm mb-4"
              >
                Create Service API Key
              </button>

              {loadingServiceApiKeys ? (
                <div className="text-center py-8">
                  <LoadingSpinner text="Loading API keys..." />
                </div>
              ) : serviceApiKeys.length === 0 ? (
                <p className="text-content-tertiary text-center py-8">No service API keys yet. Create one to enable backend operations.</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Key Prefix</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Permissions</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Last Used</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {serviceApiKeys.map((key) => (
                      <tr key={key.id}>
                        <td className="px-4 py-2 text-sm text-content-primary font-medium">{key.name}</td>
                        <td className="px-4 py-2 text-sm text-content-tertiary font-mono">{key.key_prefix}...</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {key.permissions.map(p => (
                              <span key={p} className="px-2 py-0.5 text-xs bg-[var(--accent-tint,#142330)] text-[var(--accent,#3D8FBF)] rounded">
                                {p.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            key.status === 'active' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                          }`}>
                            {key.status.charAt(0).toUpperCase() + key.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-content-tertiary">
                          {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}
                        </td>
                        <td className="px-4 py-2">
                          {key.status === 'active' && (
                            <button
                              onClick={() => revokeServiceApiKey(key.id, key.name)}
                              className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Create Service API Key Modal */}
        {showCreateServiceApiKeyModal && selectedService && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              {createdServiceApiKey ? (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">Service API Key Created</h3>
                  <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 dark:text-green-300 mb-2 font-medium">Save this key - it won't be shown again!</p>
                    <code className="block bg-surface-primary border rounded p-2 text-sm break-all select-all">
                      {createdServiceApiKey}
                    </code>
                  </div>
                  <p className="text-sm text-content-tertiary mb-4">
                    Use this key in the <code className="bg-surface-tertiary px-1 rounded">X-Service-Key</code> header when calling service operations.
                  </p>
                  <button onClick={closeServiceApiKeyModal} className="btn-primary w-full">
                    Done
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">
                    Create Service API Key for: {selectedService.name}
                  </h3>
                  <form onSubmit={handleCreateServiceApiKey} className="space-y-4">
                    <div>
                      <label htmlFor="svcKeyName" className="block text-sm font-medium text-content-primary mb-1">
                        Key Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="svcKeyName"
                        type="text"
                        value={newServiceApiKeyName}
                        onChange={(e) => setNewServiceApiKeyName(e.target.value)}
                        placeholder="e.g., Production Backend"
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="svcKeyDesc" className="block text-sm font-medium text-content-primary mb-1">
                        Description
                      </label>
                      <input
                        id="svcKeyDesc"
                        type="text"
                        value={newServiceApiKeyDescription}
                        onChange={(e) => setNewServiceApiKeyDescription(e.target.value)}
                        placeholder="e.g., Backend service for user onboarding"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-content-primary mb-2">
                        Permissions <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {[
                          { id: 'create_organization', label: 'Create Organizations', desc: 'Create organizations for users' },
                          { id: 'manage_org_members', label: 'Manage Organization Members', desc: 'Add/remove members from organizations' },
                          { id: 'grant_service', label: 'Grant Service Access', desc: 'Grant this service access to organizations' },
                          { id: 'create_devices', label: 'Create Devices', desc: 'Create device tokens for this service' },
                        ].map(perm => (
                          <label key={perm.id} className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newServiceApiKeyPermissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                              className="mt-1 rounded border-border text-orbital-600 focus:ring-orbital-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-content-primary">{perm.label}</span>
                              <p className="text-xs text-content-tertiary">{perm.desc}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button type="button" onClick={closeServiceApiKeyModal} className="flex-1 btn-secondary">
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={newServiceApiKeyPermissions.length === 0}
                        className="flex-1 btn-primary disabled:opacity-50"
                      >
                        Create API Key
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Grant Organization Access Modal */}
        {showGrantOrgAccessModal && selectedService && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-content-primary mb-4">
                Grant Access to {selectedService.name}
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="orgSelect" className="block text-sm font-medium text-content-primary mb-1">
                    Select Organization
                  </label>
                  <select
                    id="orgSelect"
                    value={selectedOrgForAccess}
                    onChange={(e) => setSelectedOrgForAccess(e.target.value)}
                    className="input-field"
                  >
                    <option value="">Choose an organization...</option>
                    {orgsWithoutAccess.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-content-tertiary mt-1">All members of this organization will get access</p>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => { setShowGrantOrgAccessModal(false); setSelectedOrgForAccess(''); }}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={grantOrgAccess}
                    disabled={!selectedOrgForAccess}
                    className="flex-1 btn-primary disabled:opacity-50"
                  >
                    Grant Access
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Organization Modal */}
        {showCreateOrgModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-content-primary mb-4">Create Organization</h3>
              <form onSubmit={handleCreateOrg} className="space-y-4">
                <div>
                  <label htmlFor="orgName" className="block text-sm font-medium text-content-primary mb-1">
                    Organization Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="orgName"
                    type="text"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    placeholder="e.g., Acme Inc."
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="orgOwner" className="block text-sm font-medium text-content-primary mb-1">
                    Owner (Optional)
                  </label>
                  <select
                    id="orgOwner"
                    value={newOrgOwner}
                    onChange={(e) => setNewOrgOwner(e.target.value)}
                    className="input-field"
                  >
                    <option value="">No owner (add later)</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.email} ({user.name?.first} {user.name?.last})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-content-tertiary mt-1">The owner will have full control of the organization</p>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => { setShowCreateOrgModal(false); setNewOrgName(''); setNewOrgOwner(''); }}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn-primary">
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Organization Members Modal */}
        {showOrgMembersModal && selectedOrg && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-auto">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-medium text-content-primary">Manage Members: {selectedOrg.name}</h3>
                  <p className="text-sm text-content-tertiary">
                    Invite Code: <span className="font-mono font-medium">{selectedOrg.code || 'N/A'}</span>
                  </p>
                </div>
                <button
                  onClick={() => { setShowOrgMembersModal(false); setSelectedOrg(null); setOrgMembers([]); }}
                  className="text-content-tertiary hover:text-content-secondary"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <button
                onClick={() => setShowAddOrgMemberModal(true)}
                className="btn-primary text-sm mb-4"
              >
                Add Member
              </button>

              {loadingOrgMembers ? (
                <div className="text-center py-8">
                  <LoadingSpinner text="Loading members..." />
                </div>
              ) : orgMembers.length === 0 ? (
                <p className="text-content-tertiary text-center py-8">No members in this organization yet.</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Role</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {orgMembers.map((member) => (
                      <tr key={member.id}>
                        <td className="px-4 py-2 text-sm text-content-primary">{member.email}</td>
                        <td className="px-4 py-2 text-sm text-content-secondary">
                          {member.name?.first} {member.name?.last}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            member.role === 'owner' ? 'bg-[var(--gold-tint,#262620)] text-[var(--accent-gold,#D4A73C)]' :
                            member.role === 'admin' ? 'bg-[var(--accent-tint,#142330)] text-[var(--accent,#3D8FBF)]' :
                            'bg-surface-tertiary text-content-secondary'
                          }`}>
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </span>
                          {member.default_organization === selectedOrg?.id && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium rounded bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                              Default
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 space-x-2">
                          {member.default_organization !== selectedOrg?.id && (
                            <button
                              onClick={() => setMemberDefaultOrg(member.id, selectedOrg!.id)}
                              className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => removeOrgMember(member.id, member.email)}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Add Organization Member Modal */}
        {showAddOrgMemberModal && selectedOrg && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-content-primary mb-4">
                Add Member to {selectedOrg.name}
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="orgUserSelect" className="block text-sm font-medium text-content-primary mb-1">
                    Select User
                  </label>
                  <select
                    id="orgUserSelect"
                    value={selectedUserForOrg}
                    onChange={(e) => setSelectedUserForOrg(e.target.value)}
                    className="input-field"
                  >
                    <option value="">Choose a user...</option>
                    {users.filter(u => !orgMembers.some(m => m.id === u.id)).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.email} ({user.name?.first} {user.name?.last})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="orgRoleSelect" className="block text-sm font-medium text-content-primary mb-1">
                    Role
                  </label>
                  <select
                    id="orgRoleSelect"
                    value={selectedRoleForOrg}
                    onChange={(e) => setSelectedRoleForOrg(e.target.value)}
                    className="input-field"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => { setShowAddOrgMemberModal(false); setSelectedUserForOrg(''); setSelectedRoleForOrg('member'); }}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addOrgMember}
                    disabled={!selectedUserForOrg}
                    className="flex-1 btn-primary disabled:opacity-50"
                  >
                    Add Member
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Device Modal */}
        {showCreateDeviceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              {createdDeviceToken ? (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">Device Token Created</h3>
                  <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 dark:text-green-300 mb-2 font-medium">Save this token - it won't be shown again!</p>
                    <code className="block bg-surface-primary border rounded p-2 text-sm break-all select-all">
                      {createdDeviceToken}
                    </code>
                  </div>
                  <p className="text-sm text-content-tertiary mb-4">
                    Use this token in the <code className="bg-surface-tertiary px-1 rounded">X-Device-Token</code> header when calling the API.
                  </p>
                  <button onClick={closeDeviceModal} className="btn-primary w-full">
                    Done
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">Register Device</h3>
                  <form onSubmit={handleCreateDevice} className="space-y-4">
                    <div>
                      <label htmlFor="deviceName" className="block text-sm font-medium text-content-primary mb-1">
                        Device Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="deviceName"
                        type="text"
                        value={newDeviceName}
                        onChange={(e) => setNewDeviceName(e.target.value)}
                        placeholder="e.g., Temperature Sensor #1"
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="deviceOrg" className="block text-sm font-medium text-content-primary mb-1">
                        Organization <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="deviceOrg"
                        value={newDeviceOrgId}
                        onChange={(e) => setNewDeviceOrgId(e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Select organization...</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="deviceService" className="block text-sm font-medium text-content-primary mb-1">
                        Service <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="deviceService"
                        value={newDeviceServiceId}
                        onChange={(e) => setNewDeviceServiceId(e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Select service...</option>
                        {services.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="deviceType" className="block text-sm font-medium text-content-primary mb-1">
                        Device Type
                      </label>
                      <select
                        id="deviceType"
                        value={newDeviceType}
                        onChange={(e) => setNewDeviceType(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Select type (optional)</option>
                        {deviceTypes.filter(t => t.category === 'iot').length > 0 && (
                          <optgroup label="IoT Devices">
                            {deviceTypes.filter(t => t.category === 'iot').map(t => (
                              <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                          </optgroup>
                        )}
                        {deviceTypes.filter(t => t.category === 'infrastructure').length > 0 && (
                          <optgroup label="Infrastructure">
                            {deviceTypes.filter(t => t.category === 'infrastructure').map(t => (
                              <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                          </optgroup>
                        )}
                        {deviceTypes.filter(t => t.category === 'client').length > 0 && (
                          <optgroup label="Client Software">
                            {deviceTypes.filter(t => t.category === 'client').map(t => (
                              <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                          </optgroup>
                        )}
                        {deviceTypes.filter(t => !t.category).length > 0 && (
                          <optgroup label="Other">
                            {deviceTypes.filter(t => !t.category).map(t => (
                              <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="deviceHardwareId" className="block text-sm font-medium text-content-primary mb-1">
                        Hardware ID
                      </label>
                      <input
                        id="deviceHardwareId"
                        type="text"
                        value={newDeviceHardwareId}
                        onChange={(e) => setNewDeviceHardwareId(e.target.value)}
                        placeholder="e.g., serial number, MAC address"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label htmlFor="deviceDesc" className="block text-sm font-medium text-content-primary mb-1">
                        Description
                      </label>
                      <textarea
                        id="deviceDesc"
                        value={newDeviceDescription}
                        onChange={(e) => setNewDeviceDescription(e.target.value)}
                        placeholder="Optional description..."
                        className="input-field"
                        rows={2}
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button type="button" onClick={closeDeviceModal} className="flex-1 btn-secondary">
                        Cancel
                      </button>
                      <button type="submit" className="flex-1 btn-primary">
                        Create Device
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Create Application Modal */}
        {showCreateAppModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              {createdAppCredentials ? (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">Application Credentials</h3>
                  <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 dark:text-green-300 mb-2 font-medium">Save these credentials - the secret won't be shown again!</p>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-content-tertiary block">Client ID</span>
                        <code className="block bg-surface-primary border rounded p-2 text-sm break-all select-all">
                          {createdAppCredentials.client_id}
                        </code>
                      </div>
                      <div>
                        <span className="text-xs text-content-tertiary block">Client Secret</span>
                        <code className="block bg-surface-primary border rounded p-2 text-sm break-all select-all">
                          {createdAppCredentials.client_secret}
                        </code>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-content-tertiary mb-4">
                    Use these in a <code className="bg-surface-tertiary px-1 rounded">POST /v1/auth/token</code> request with <code className="bg-surface-tertiary px-1 rounded">grant_type: client_credentials</code>.
                  </p>
                  <button onClick={closeAppModal} className="btn-primary w-full">
                    Done
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">Register Application</h3>
                  <form onSubmit={handleCreateApp} className="space-y-4">
                    <div>
                      <label htmlFor="appName" className="block text-sm font-medium text-content-primary mb-1">
                        Application Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="appName"
                        type="text"
                        value={newAppName}
                        onChange={(e) => setNewAppName(e.target.value)}
                        placeholder="e.g., My Chatbot"
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="appDesc" className="block text-sm font-medium text-content-primary mb-1">
                        Description
                      </label>
                      <input
                        id="appDesc"
                        type="text"
                        value={newAppDescription}
                        onChange={(e) => setNewAppDescription(e.target.value)}
                        placeholder="e.g., Internal support chatbot"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label htmlFor="appOrg" className="block text-sm font-medium text-content-primary mb-1">
                        Organization <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="appOrg"
                        value={newAppOrgId}
                        onChange={(e) => setNewAppOrgId(e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Select organization...</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-content-primary mb-2">
                        Allowed Services
                      </label>
                      <div className="space-y-2">
                        {services.map(svc => (
                          <label key={svc.id} className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newAppServices.includes(svc.id)}
                              onChange={() => toggleAppService(svc.id)}
                              className="rounded border-border text-orbital-600 focus:ring-orbital-500"
                            />
                            <span className="text-sm text-content-primary">{svc.name}</span>
                          </label>
                        ))}
                        {services.length === 0 && (
                          <p className="text-xs text-content-tertiary">No services registered. The app will have no service restrictions.</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="appTier" className="block text-sm font-medium text-content-primary mb-1">
                        Rate Limit Tier
                      </label>
                      <select
                        id="appTier"
                        value={newAppTier}
                        onChange={(e) => setNewAppTier(e.target.value)}
                        className="input-field"
                      >
                        <option value="starter">Starter (60/min, 5K/day)</option>
                        <option value="pro">Pro (600/min, 50K/day)</option>
                        <option value="enterprise">Enterprise (6K/min, 500K/day)</option>
                      </select>
                    </div>
                    <div className="flex space-x-3">
                      <button type="button" onClick={closeAppModal} className="flex-1 btn-secondary">
                        Cancel
                      </button>
                      <button type="submit" className="flex-1 btn-primary">
                        Register
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Device Details Modal */}
        {showDeviceDetailsModal && selectedDevice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-content-primary">{selectedDevice.name}</h3>
                <button
                  onClick={() => { setShowDeviceDetailsModal(false); setSelectedDevice(null); }}
                  className="text-content-tertiary hover:text-content-secondary"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-content-tertiary">Status</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    selectedDevice.status === 'active' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' :
                    selectedDevice.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                  }`}>
                    {selectedDevice.status.charAt(0).toUpperCase() + selectedDevice.status.slice(1)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-content-tertiary block">Organization</span>
                    <span className="font-medium">{selectedDevice.organization_name || selectedDevice.organization_id}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Service</span>
                    <span className="font-medium">{selectedDevice.service_name || selectedDevice.service_id}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Type</span>
                    <span className="font-medium">{selectedDevice.device_type || '-'}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Hardware ID</span>
                    <span className="font-medium font-mono text-xs">{selectedDevice.hardware_id || '-'}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Token Prefix</span>
                    <span className="font-medium font-mono">{selectedDevice.token_prefix ? `${selectedDevice.token_prefix}...` : '-'}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Last Seen</span>
                    <span className="font-medium">
                      {selectedDevice.last_seen_at ? new Date(selectedDevice.last_seen_at).toLocaleString() : 'Never'}
                    </span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Created</span>
                    <span className="font-medium">{new Date(selectedDevice.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Firmware</span>
                    <span className="font-medium">{selectedDevice.firmware_version || '-'}</span>
                  </div>
                </div>

                {selectedDevice.description && (
                  <div>
                    <span className="text-sm text-content-tertiary block">Description</span>
                    <p className="text-sm">{selectedDevice.description}</p>
                  </div>
                )}

                <div className="border-t pt-4 flex flex-wrap gap-2">
                  {selectedDevice.status !== 'revoked' && (
                    <button
                      onClick={() => regenerateDeviceToken(selectedDevice.id, selectedDevice.name)}
                      className="btn-secondary text-sm"
                    >
                      Regenerate Token
                    </button>
                  )}
                  {selectedDevice.status === 'active' && (
                    <button
                      onClick={() => { suspendDevice(selectedDevice.id, selectedDevice.name); setShowDeviceDetailsModal(false); }}
                      className="text-sm px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200"
                    >
                      Suspend
                    </button>
                  )}
                  {selectedDevice.status === 'suspended' && (
                    <button
                      onClick={() => { reactivateDevice(selectedDevice.id); setShowDeviceDetailsModal(false); }}
                      className="text-sm px-4 py-2 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 rounded-lg hover:bg-green-200"
                    >
                      Reactivate
                    </button>
                  )}
                  {selectedDevice.status !== 'revoked' && (
                    <button
                      onClick={() => revokeDevice(selectedDevice.id, selectedDevice.name)}
                      className="text-sm px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 rounded-lg hover:bg-red-200"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* KYC Tab */}
        {activeTab === 'kyc' && (
          <KycPanel />
        )}
        {/* Agent Sessions Tab */}
        {activeTab === 'sessions' && (
          <AgentSessionsPanel />
        )}
        {activeTab === 'idp' && (
          <AdminIdPManager />
        )}
      </div>
    </div>
  );
}


/* ============================================================================
 * Agent Sessions Panel (self-contained)
 * ============================================================================ */

function AgentSessionsPanel() {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'active' | 'all'>('active');

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/admin/agent-sessions?active_only=${filter === 'active'}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (err: any) {
      // Fallback: try the DB directly through admin endpoint
      try {
        const res = await fetch(`${config.gatewayUrl}/v1/admin/agent-sessions-list?active_only=${filter === 'active'}`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setSessions(data.sessions || []);
        } else {
          setError('Agent sessions endpoint not available yet');
          setSessions([]);
        }
      } catch {
        setError('Agent sessions endpoint not available yet');
        setSessions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [filter]);

  const revokeSession = async (sessionId: string) => {
    if (!confirm('Revoke this agent session?')) return;
    try {
      await fetch(`${config.gatewayUrl}/v1/agent/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchSessions();
    } catch {
      setError('Failed to revoke session');
    }
  };

  const formatDate = (d: string | null) => d ? new Date(d).toLocaleString() : '—';

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      revoked: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors.expired}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-content-primary">Agent Sessions</h3>
        <div className="flex items-center space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'active' | 'all')}
            className="rounded-lg border border-border bg-surface-primary text-content-primary text-sm px-3 py-1.5"
          >
            <option value="active">Active only</option>
            <option value="all">All sessions</option>
          </select>
          <button
            onClick={fetchSessions}
            className="text-sm px-3 py-1.5 bg-orbital-600 text-white rounded-lg hover:bg-orbital-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : sessions.length === 0 ? (
        <div className="card text-center py-8 text-content-tertiary">
          No {filter === 'active' ? 'active ' : ''}agent sessions found
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Session</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Device</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Scope</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Depth</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sessions.map((s: any) => (
                <tr key={s.session_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm">
                    <div className="font-mono text-xs text-content-secondary">{s.session_id?.slice(0, 8)}...</div>
                    <div className="text-xs text-content-tertiary">{s.portable ? 'opj_ (JWT)' : 'ops_ (opaque)'}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-mono text-xs">{s.device_id?.slice(0, 8)}...</div>
                    <div className="text-xs text-content-tertiary">{s.organization_id}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="text-xs">
                      {s.scope?.allowed_services?.join(', ') || '—'}
                    </div>
                    <div className="text-xs text-content-tertiary">
                      {s.scope?.allowed_actions?.length || 0} actions
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-center">{s.delegation_depth}</td>
                  <td className="px-4 py-3 text-sm">{getStatusBadge(s.status)}</td>
                  <td className="px-4 py-3 text-sm text-xs">{formatDate(s.expires_at)}</td>
                  <td className="px-4 py-3 text-sm">
                    {s.status === 'active' && (
                      <button
                        onClick={() => revokeSession(s.session_id)}
                        className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 rounded hover:bg-red-200"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ============================================================================
 * KYC Management Panel (self-contained)
 * ============================================================================ */

interface KycUser {
  user_id: string;
  email: string | null;
  kyc_level: number;
  status: string;
  provider: string | null;
  verified_at: string | null;
  expires_at: string | null;
}

function KycPanel() {
  const [users, setUsers] = useState<KycUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [showSetLevelModal, setShowSetLevelModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);
  const [newLevel, setNewLevel] = useState(1);
  const [setLevelReason, setSetLevelReason] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = filter
        ? `${config.gatewayUrl}/v1/admin/kyc/users?status=${filter}`
        : `${config.gatewayUrl}/v1/admin/kyc/users`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load KYC users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const setKycLevel = async () => {
    if (!selectedUserId) return;
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/admin/kyc/${selectedUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ kyc_level: newLevel, reason: setLevelReason || undefined }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowSetLevelModal(false);
      setSetLevelReason('');
      fetchUsers();
    } catch {
      setError('Failed to set KYC level');
    }
  };

  const revokeKyc = async (userId: string) => {
    if (!confirm('Revoke KYC verification for this user? This will reset their level to 0.')) return;
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/admin/kyc/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchUsers();
    } catch {
      setError('Failed to revoke KYC');
    }
  };

  const getLevelLabel = (level: number) => {
    switch (level) {
      case 0: return 'None';
      case 1: return 'Basic';
      case 2: return 'Enhanced';
      case 3: return 'Full';
      default: return `Level ${level}`;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      verified: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
      expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors.expired}`}>
        {status}
      </span>
    );
  };

  const formatDate = (d: string | null) => d ? new Date(d).toLocaleString() : '—';

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-content-primary">KYC Verifications</h3>
        <div className="flex items-center space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input text-sm py-1"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </select>
          <button onClick={fetchUsers} className="btn-secondary text-sm py-1">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <LoadingSpinner text="Loading KYC data..." />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-8 text-content-tertiary">
          No KYC verifications found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">Level</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">Verified</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((user) => (
                <tr key={user.user_id} className="hover:bg-surface-secondary">
                  <td className="px-4 py-3 text-sm">
                    <div className="text-content-primary">{user.email || 'Unknown'}</div>
                    <div className="text-xs text-content-tertiary font-mono">{user.user_id.slice(0, 8)}...</div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`font-medium ${
                      user.kyc_level >= 3 ? 'text-green-600 dark:text-green-400' :
                      user.kyc_level >= 2 ? 'text-blue-600 dark:text-blue-400' :
                      user.kyc_level >= 1 ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-content-tertiary'
                    }`}>
                      {getLevelLabel(user.kyc_level)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{getStatusBadge(user.status)}</td>
                  <td className="px-4 py-3 text-sm text-content-secondary">{user.provider || '—'}</td>
                  <td className="px-4 py-3 text-sm text-content-secondary">{formatDate(user.verified_at)}</td>
                  <td className="px-4 py-3 text-sm space-x-2">
                    <button
                      onClick={() => {
                        setSelectedUserId(user.user_id);
                        setSelectedUserEmail(user.email);
                        setNewLevel(Math.min(user.kyc_level + 1, 3));
                        setShowSetLevelModal(true);
                      }}
                      className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
                    >
                      Set Level
                    </button>
                    {user.status === 'verified' && (
                      <button
                        onClick={() => revokeKyc(user.user_id)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Set Level Modal */}
      {showSetLevelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSetLevelModal(false)}>
          <div className="bg-surface-primary rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-content-primary mb-4">
              Set KYC Level
            </h3>
            <p className="text-sm text-content-secondary mb-4">
              User: {selectedUserEmail || selectedUserId}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">KYC Level</label>
                <select
                  value={newLevel}
                  onChange={(e) => setNewLevel(Number(e.target.value))}
                  className="input w-full"
                >
                  <option value={0}>0 — None</option>
                  <option value={1}>1 — Basic</option>
                  <option value={2}>2 — Enhanced</option>
                  <option value={3}>3 — Full</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={setLevelReason}
                  onChange={(e) => setSetLevelReason(e.target.value)}
                  className="input w-full"
                  placeholder="Manual verification, ID check, etc."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button onClick={() => setShowSetLevelModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={setKycLevel} className="btn-primary">
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
